// مشترک تبادل استاندارد با آزمایشگاه و مرکز تصویربرداری (AP-INT-05؛ US-MED-043، 051، 056): کلیدهای Query، برچسب
// وضعیت صف «نتایج نامعلوم» و دریافت فریم محافظت‌شده DICOM. متن‌ها فقط از فضای نام med.
import type { MedInboundLabStatus } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { StatusChip, type StatusTone } from '@phoenix/ui';
import { useEffect, useState } from 'react';

/** صف نتایج نامعلوم (US-MED-051). */
export const SCR_STAFF_703 = 'SCR-STAFF-703';

/** نمایشگر DICOM (US-MED-056). */
export const SCR_STAFF_704 = 'SCR-STAFF-704';

/** مسیر صف نتایج نامعلوم. */
export const INBOUND_LAB_PATH = '/desk/inbound-lab-messages';

export const labExchangeKeys = {
  inbound: (tenantId: string | undefined, status: string) => ['medorders', tenantId, 'inbound-lab', status] as const,
  studies: (tenantId: string | undefined, resultId: string) => ['medorders', tenantId, 'imaging-studies', resultId] as const,
  series: (tenantId: string | undefined, resultId: string, studyInstanceUid: string) =>
    ['medorders', tenantId, 'imaging-series', resultId, studyInstanceUid] as const,
};

const INBOUND_TONE: Record<MedInboundLabStatus, StatusTone> = {
  Unmatched: 'pending',
  Matched: 'done',
  Discarded: 'cancelled',
};

/** برچسب وضعیت یک پیام نتیجه ورودی. */
export function InboundStatusChip({ status }: { status: MedInboundLabStatus }) {
  return <StatusChip tone={INBOUND_TONE[status] ?? 'neutral'} labelKey={`med:inbound_lab_status.${status}`} />;
}

/** عدد از قرارداد (double ممکن است رشته باشد). */
export function decimal(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export interface ImagingFrameQuery {
  resultId: string;
  studyInstanceUid: string;
  seriesInstanceUid?: string | null;
  sopInstanceUid?: string | null;
  frame: number;
  windowCenter?: number | null;
  windowWidth?: number | null;
}

/**
 * فریم DICOM محافظت‌شده (AC-MED-056.1): تصویر با توکن دریافت و فقط به‌صورت Object URL در حافظه صفحه نگه داشته
 * می‌شود؛ هیچ ذخیره‌سازی محلی (localStorage، IndexedDB، Cache) ندارد و URL هنگام تغییر یا Unmount آزاد می‌شود.
 * پاسخ سرور <c>no-store</c> است و هر مشاهده در سرور Audit می‌شود (CLAUDE.md §۱۰).
 */
export function useImagingFrame(query: ImagingFrameQuery | null) {
  const { tenantApi } = useTenantSession();
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const signature = query
    ? [
        query.resultId,
        query.studyInstanceUid,
        query.seriesInstanceUid ?? '',
        query.sopInstanceUid ?? '',
        query.frame,
        query.windowCenter ?? '',
        query.windowWidth ?? '',
      ].join('|')
    : null;

  useEffect(() => {
    if (!query || !signature) {
      setUrl(null);
      return undefined;
    }
    let cancelled = false;
    let created: string | null = null;
    setLoading(true);
    setError(null);
    void tenantApi
      .GET('/api/v1/medical-orders/results/{resultId}/imaging-frame', {
        params: {
          path: { resultId: query.resultId },
          query: {
            studyInstanceUid: query.studyInstanceUid,
            seriesInstanceUid: query.seriesInstanceUid ?? undefined,
            sopInstanceUid: query.sopInstanceUid ?? undefined,
            frame: query.frame,
            windowCenter: query.windowCenter ?? undefined,
            windowWidth: query.windowWidth ?? undefined,
          },
        },
        parseAs: 'blob',
        cache: 'no-store',
      })
      .then((result) => {
        if (cancelled) {
          return;
        }
        setLoading(false);
        if (!result.response.ok || typeof URL.createObjectURL !== 'function') {
          setError(result.error ?? new Error(String(result.response.status)));
          return;
        }
        created = URL.createObjectURL(result.data as unknown as Blob);
        setUrl(created);
      })
      .catch((reason: unknown) => {
        if (!cancelled) {
          setLoading(false);
          setError(reason);
        }
      });
    return () => {
      cancelled = true;
      if (created) {
        URL.revokeObjectURL(created);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- امضای پرس‌وجو جای شیء ناپایدار را می‌گیرد.
  }, [tenantApi, signature]);

  return { url, error, loading };
}
